/**
 * Factory presets for SynthNode (Tone.js)
 */

export interface SynthPreset {
  name: string;
  description: string;
  params: {
    oscillator: string;
    attack: number;
    decay: number;
    sustain: number;
    release: number;
    volume?: number;
  };
}

export const SYNTH_PRESETS: SynthPreset[] = [
  // ─────────────────────────────────────────────────────────────
  // Lead Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Classic Lead',
    description: 'Punchy sawtooth lead with quick attack',
    params: {
      oscillator: 'sawtooth',
      attack: 0.01,
      decay: 0.1,
      sustain: 0.7,
      release: 0.3
    }
  },
  {
    name: 'Hollow Lead',
    description: 'Square wave with soft attack for a rounder tone',
    params: {
      oscillator: 'square',
      attack: 0.05,
      decay: 0.15,
      sustain: 0.6,
      release: 0.4
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Bass Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Sub Bass',
    description: 'Pure sine wave for deep sub frequencies',
    params: {
      oscillator: 'sine',
      attack: 0.02,
      decay: 0.1,
      sustain: 0.9,
      release: 0.5
    }
  },
  {
    name: 'Growl Bass',
    description: 'Aggressive sawtooth bass with bite',
    params: {
      oscillator: 'sawtooth',
      attack: 0.01,
      decay: 0.3,
      sustain: 0.4,
      release: 0.2
    }
  },
  {
    name: 'Pluck Bass',
    description: 'Punchy bass with body for rhythmic patterns',
    params: {
      oscillator: 'sawtooth',
      attack: 0.01,
      decay: 0.25,
      sustain: 0.3,
      release: 0.2
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Pad Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Warm Pad',
    description: 'Slow attack triangle pad for background textures',
    params: {
      oscillator: 'triangle',
      attack: 0.8,
      decay: 0.5,
      sustain: 0.7,
      release: 2.0
    }
  },
  {
    name: 'String Pad',
    description: 'Sawtooth ensemble for lush textures',
    params: {
      oscillator: 'sawtooth',
      attack: 0.5,
      decay: 0.3,
      sustain: 0.8,
      release: 1.5
    }
  },
  {
    name: 'Glass Pad',
    description: 'Bright sine pad, ethereal and crystalline',
    params: {
      oscillator: 'sine',
      attack: 1.0,
      decay: 0.2,
      sustain: 0.6,
      release: 3.0
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Pluck / Percussion
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Bell Pluck',
    description: 'Quick decay for bell-like tones',
    params: {
      oscillator: 'triangle',
      attack: 0.001,
      decay: 0.3,
      sustain: 0.0,
      release: 0.5
    }
  },
  {
    name: 'Mellow Pluck',
    description: 'Soft triangle pluck with gentle decay',
    params: {
      oscillator: 'triangle',
      attack: 0.005,
      decay: 0.35,
      sustain: 0.0,
      release: 0.4
    }
  },
  {
    name: 'Arp Blip',
    description: 'Ultra-short blip for fast arpeggios',
    params: {
      oscillator: 'sawtooth',
      attack: 0.001,
      decay: 0.05,
      sustain: 0.0,
      release: 0.05
    }
  }
];

export function getSynthPreset(name: string): SynthPreset | undefined {
  return SYNTH_PRESETS.find(p => p.name === name);
}

export function getSynthPresetNames(): string[] {
  return SYNTH_PRESETS.map(p => p.name);
}
